import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo
} from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticated } from '@gettactic/hooks';
import { api } from '@/lib/api/api';
import { getItem, setItem } from '@/lib/storage';

const IMPERSONATE_STORE = 'impersonate';
const IMPERSONATE_SIDEBAR_ENABLED_KEY = 'sidebarButtonEnabled';

type ImpersonateContextType = {
  isBeingImpersonated: boolean;
  canImpersonate: boolean;
  impersonate: (email: string) => void;
  stopImpersonating: () => void;
  isWidgetVisible: boolean;
  toggleWidget: () => void;
  setIsWidgetVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isSidebarButtonEnabled: boolean;
  toggleSidebarButton: () => void;
};

const ImpersonateContext = createContext<ImpersonateContextType | undefined>(
  undefined
);

export const ImpersonateProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const queryClient = useQueryClient();

  const [isWidgetVisible, setIsWidgetVisible] = useState<boolean>(false);
  const [isSidebarButtonEnabled, setIsSidebarButtonEnabled] =
    useState<boolean>(false);

  useEffect(() => {
    const storedValue = getItem<boolean>(IMPERSONATE_STORE, IMPERSONATE_SIDEBAR_ENABLED_KEY);
    if (storedValue !== null) {
      setIsSidebarButtonEnabled(storedValue);
    }
  }, []);

  const toggleSidebarButton = useCallback(() => {
    setIsSidebarButtonEnabled((prev) => {
      const newState = !prev;
      setItem(IMPERSONATE_STORE, IMPERSONATE_SIDEBAR_ENABLED_KEY, newState);
      if (!newState) {
        setIsWidgetVisible(false);
      }
      return newState;
    });
  }, []);

  const toggleWidget = useCallback(() => {
    setIsWidgetVisible((prev) => !prev);
  }, []);

  const isBeingImpersonated = !!authenticatedUser?.user?.is_being_impersonated;
  const canImpersonate = !!authenticatedUser?.permissions?.impersonate;

  const impersonateMutation = useMutation(
    async ({ email }: { email: string }) => {
      await api.client.users.impersonate({ email });
    },
    {
      onSuccess: () => {
        setItem(IMPERSONATE_STORE, IMPERSONATE_SIDEBAR_ENABLED_KEY, true);
        setIsSidebarButtonEnabled(true);
        queryClient.invalidateQueries();
        window.location.assign('/');
      }
    }
  );

  const stopMutation = useMutation(
    async () => {
      await fetch(
        `${process.env.NEXT_PUBLIC_TACTIC_API_URL}/auth/impersonate/end`,
        {
          credentials: 'include'
        }
      );
    },
    {
      onSuccess: () => {
        window.location.assign('/');
      },
      onError: () => {
        window.location.assign('/');
      }
    }
  );

  const impersonate = (email: string) => {
    if (!canImpersonate) {
      return;
    }
    const trimmedEmail = email.trim().toLowerCase();
    if (trimmedEmail) {
      impersonateMutation.mutate({ email: trimmedEmail });
    }
  };

  const stopImpersonating = () => {
    stopMutation.mutate();
  };

  const value = useMemo(
    () => ({
      isBeingImpersonated,
      canImpersonate,
      impersonate,
      stopImpersonating,
      isWidgetVisible,
      toggleWidget,
      setIsWidgetVisible,
      isSidebarButtonEnabled,
      toggleSidebarButton
    }),
    [
      isBeingImpersonated,
      canImpersonate,
      isWidgetVisible,
      isSidebarButtonEnabled,
      toggleSidebarButton,
      toggleWidget
    ]
  );

  return (
    <ImpersonateContext.Provider value={value}>
      {children}
    </ImpersonateContext.Provider>
  );
};

export const useImpersonateContext = () => {
  const context = useContext(ImpersonateContext);
  if (context === undefined) {
    throw new Error(
      'useImpersonateContext must be used within an ImpersonateProvider'
    );
  }
  return context;
}; 